import { MELODY_PHRASES } from "./harmonyEngine";

export interface SessionPersonality {
  bpm: number;
  progressionPool: number[];  // indices into PROGRESSIONS
  registerOffset: number;     // semitone shift for chord voicing anchor (-12, 0, or 12)
  melodyPhrasePool: number[]; // indices into MELODY_PHRASES
  rollChance: number;         // per-chord probability of rolling
  passingChordChance: number; // per-chord probability of passing chord
  guitarBias: number;         // probability of guitar as melody instrument
}

interface Mood {
  name: string;
  bpmRange: [number, number];
  progressionPool: number[];
  registerOffset: number;
  melodyPhrasePool: number[];
  rollChance: number;
  passingChordChance: number;
  guitarBias: number;
}

// Phrase indices by character (see MELODY_PHRASES in harmonyEngine.ts):
// 0=ascent  1=high arch  2=question  3=descent  4=rocking  5=sparse high
// 6=syncopated  7=late entry  8=stepwise fall  9=pickup  10=delayed rock  11=oscillate

// Progression indices (see PROGRESSIONS in harmonyEngine.ts):
// 0=ii–V–I–I  1=I–vi–ii–V  2=i–♭VII–♭VI–V  3=I–IV–iii–♭VII
// 4=I–♭VII–IV–I  5=I–iii–IV–V  6=I–V–vi–IV  7=I–iii–vi–IV

const MOODS: Mood[] = [
  {
    name: "mellow",
    bpmRange: [60, 68],
    progressionPool: [0, 2, 3],        // jazz standard, minor neo-soul, modal borrowed
    registerOffset: -12,
    melodyPhrasePool: [3, 5, 7],       // descent, sparse high, late entry
    rollChance: 0.7,
    passingChordChance: 0.3,
    guitarBias: 0.5,
  },
  {
    name: "dreamy",
    bpmRange: [72, 80],
    progressionPool: [1, 4, 7],        // I–vi–ii–V, I–♭VII–IV–I, I–iii–vi–IV
    registerOffset: 0,
    melodyPhrasePool: [1, 5, 11],      // high arch, sparse high, oscillate
    rollChance: 0.5,
    passingChordChance: 0.4,
    guitarBias: 0.4,
  },
  {
    name: "upbeat",
    bpmRange: [82, 92],
    progressionPool: [5, 6, 7],        // all bright major
    registerOffset: 0,
    melodyPhrasePool: [0, 6, 9],       // ascent, syncopated, pickup
    rollChance: 0.3,
    passingChordChance: 0.6,
    guitarBias: 0.2,
  },
  {
    name: "late night",
    bpmRange: [65, 75],
    progressionPool: [0, 2, 4],        // jazz standard, minor neo-soul, major plagal
    registerOffset: -12,
    melodyPhrasePool: [7, 8, 11],      // late entry, stepwise fall, oscillate
    rollChance: 0.6,
    passingChordChance: 0.4,
    guitarBias: 0.5,
  },
  {
    name: "bright",
    bpmRange: [78, 88],
    progressionPool: [1, 5, 6, 7],     // all bright, major-tonic progressions
    registerOffset: 12,
    melodyPhrasePool: [0, 2, 4],       // ascent, question, rocking
    rollChance: 0.2,
    passingChordChance: 0.7,
    guitarBias: 0.35,
  },
];

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function pickSessionPersonality(): SessionPersonality {
  const mood = MOODS[Math.floor(Math.random() * MOODS.length)];
  return {
    bpm: randInt(mood.bpmRange[0], mood.bpmRange[1]),
    progressionPool: mood.progressionPool,
    registerOffset: mood.registerOffset,
    melodyPhrasePool: mood.melodyPhrasePool.filter(i => i < MELODY_PHRASES.length),
    rollChance: mood.rollChance,
    passingChordChance: mood.passingChordChance,
    guitarBias: mood.guitarBias,
  };
}
